import { ApiLogEntry } from "./types";
import { getActiveEnvironment, getApiKey, getDryRun } from "./session";

type LogSink = (entry: ApiLogEntry) => void;

let logSink: LogSink = () => {};

export function registerLogSink(sink: LogSink) {
  logSink = sink;
}

type FetchOpts = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  multipart?: FormData;
  query?: Record<string, string>;
};

export class ApiError extends Error {
  status: number;
  code: string;
  errors: Record<string, string> | null;
  constructor(status: number, code: string, message: string, errors: Record<string, string> | null) {
    super(message);
    this.status = status;
    this.code = code;
    this.errors = errors;
  }
}

export async function apiFetch<T = unknown>(path: string, opts: FetchOpts = {}): Promise<T> {
  const env = getActiveEnvironment();
  const key = getApiKey();
  const method = opts.method ?? "GET";

  const qs = new URLSearchParams(opts.query ?? {});
  if (getDryRun() && method !== "GET") qs.set("dry_run", "true");
  const qsStr = qs.toString();

  const url = `${env.baseUrl.replace(/\/$/, "")}${path}${qsStr ? `?${qsStr}` : ""}`;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (key) headers["Authorization"] = `Bearer ${key}`;

  let bodyInit: BodyInit | undefined;
  let requestBody: string | null = null;
  let multipartSummary: string | undefined;

  if (opts.multipart) {
    bodyInit = opts.multipart;
    const parts: string[] = [];
    opts.multipart.forEach((v, k) => {
      parts.push(v instanceof File ? `${k}=@${v.name}` : `${k}=${v}`);
    });
    multipartSummary = parts.join(" ");
  } else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    requestBody = JSON.stringify(opts.body);
    bodyInit = requestBody;
  }

  const entry: ApiLogEntry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    at: Date.now(),
    method,
    url,
    status: null,
    durationMs: null,
    requestBody,
    responseBody: null,
    requestHeaders: headers,
    multipartSummary,
  };

  const started = performance.now();
  let text = "";
  let status = 0;

  try {
    const resp = await fetch(url, { method, headers, body: bodyInit });
    status = resp.status;
    text = await resp.text();
    entry.status = status;
    entry.durationMs = Math.round(performance.now() - started);
    entry.responseBody = text;
    logSink(entry);

    if (!resp.ok) {
      let code = "http_error";
      let message = `HTTP ${status}`;
      let errors: Record<string, string> | null = null;
      try {
        const parsed = JSON.parse(text);
        code = parsed.code ?? code;
        message = parsed.message ?? message;
        errors = parsed.errors ?? null;
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(status, code, message, errors);
    }

    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  } catch (err) {
    if (entry.status === null) {
      entry.status = 0;
      entry.durationMs = Math.round(performance.now() - started);
      entry.error = err instanceof Error ? err.message : String(err);
      logSink(entry);
    }
    throw err;
  }
}
